import * as React from 'react';
import ReactFC from 'react-fusioncharts';
import Select from 'react-select';
import { Col, Collapse, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { useDebouncedCallback } from 'use-debounce';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { selectFilteredSortedBarChartDataPoints } from '../VisualizationSelectors';

const { useCallback, useMemo } = React;

import Fuse from 'fuse.js';
import FusionCharts from 'fusioncharts';
import Charts from 'fusioncharts/fusioncharts.charts';
import FusionTheme from 'fusioncharts/themes/fusioncharts.theme.fusion';
import { selectAvailableReportYears } from '../../../../redux/ReportSlice';
import {
    clearBarChartData,
    DataCriterion,
    DataPoint,
    getDataPointNodeByYearEntityAccount,
    getYearlyDataForEntities,
    selectLoadingState,
} from '../../../../redux/VisualizationSlice';

// Initialize FusionCharts with required modules
ReactFC.fcRoot(FusionCharts, Charts, FusionTheme);

const BarChartView = (props: any) => {
    const dispatch = useAppDispatch();

    const availableReportYearsSelector = useAppSelector(selectAvailableReportYears);
    const loadingStatusSelector = useAppSelector(selectLoadingState);

    // Pure, memoized derivation of the redux data filtered/sorted by the currently
    // selected entity type. This is recomputed correctly on every render/remount,
    // regardless of component lifecycle timing.
    const sortedFilteredData: Array<DataPoint> = useAppSelector((state) =>
        selectFilteredSortedBarChartDataPoints(state, props.selectedEntityType.value)
    );

    const isMounted = React.useRef(true);

    const chartIntervals = 10;

    const entityTypeValues = [
        { label: "All", value: "ALL" },
        { label: "Counties", value: "COUNTIES" },
        { label: "Cities", value: "CITIES" },
        { label: "Special Districts", value: "SPECIAL_DISTRICTS" },
        { label: "Other Entities", value: "OTHER_ENTITIES" }
    ];

    const defaultValue = { label: "please select", value: "default" };

    //Control fiscal year

    const [fiscalYearOptions, setFiscalYearOptions] = React.useState<Array<{ label: string; value: string }>>([]);

    //Chart data and pagination
    const [chartData, setChartData] = React.useState<Array<DataPoint>>([]);
    const [displayValues, setDisplayValues] = React.useState<Array<{ label: string; value: string }>>([]);
    const [currentMaxIndex, setCurrentMaxIndex] = React.useState<number>(0);
    const [maxDataValue, setMaxDataValue] = React.useState<number>(0);
    const [minDataValue, setMinDataValue] = React.useState<number>(0);

    // Entity search hooks
    const [isEntitySearchSuggestOpen, setIsEntitySearchSuggestOpen] = React.useState<boolean>(false);

    // Chart sizing
    const [chartHeight, setChartHeight] = React.useState<number>(600);

    const closeSuggestionDebounce = useDebouncedCallback(() => {
        setIsEntitySearchSuggestOpen(false);
    }, 3000);

    const clearHooks = () => {
        setChartData([]);
        setFusionChartDataFromDataPoints([]);
        setMaxDataValue(0);
        setMinDataValue(0);
        setCurrentMaxIndex(0);
    }

    const clearHooksAndSearch = () => {
        clearHooks();
        props.setEntitySearchTerm('');
    }

    const adjustShowingTotal = (newMaxIndex: number) => {
        if (currentMaxIndex !== newMaxIndex) {
            const startIndex = newMaxIndex;
            const endIndex = newMaxIndex + chartIntervals;
            const newChartDataRange = chartData.slice(startIndex, endIndex);
            setCurrentMaxIndex(newMaxIndex);
            setFusionChartDataFromDataPoints(newChartDataRange);
        }
    }

    const setFusionChartDataFromDataPoints = (topValues: Array<DataPoint>) => {

        if (!isMounted.current) return;

        let fusionDataTopValues: Array<{ label: string; value: string }> = [];

        for (let i = 0; i < topValues.length; i++) {
            let fusionDataPoint: { label: string; value: string; } = {
                label: topValues[i].x,
                value: topValues[i].y.toString()
            }
            fusionDataTopValues.push(fusionDataPoint);
        }

        setDisplayValues(fusionDataTopValues);
    }

    const fetchDataForSelectedYearAndAccountType = (fiscalYear: string, accountTypeValue: string) => {
        if (fiscalYear && fiscalYear.trim() !== "" && accountTypeValue != defaultValue.value) {

            const yearValue = Number(fiscalYear);

            if (Number.isFinite(yearValue)) {
                var criterion: DataCriterion = {
                    year: yearValue,
                    dataType: accountTypeValue as 'REVENUES' | 'EXPENDITURES' | 'LONG-TERM_DEBT' | 'POPULATION' | 'BALANCE_SHEET' | null,
                    entityType: "NULL",
                    entityId: null,
                    group: null,
                };

                dispatch(getYearlyDataForEntities(criterion));
            }
        }
    }

    const handleEntityTypeChange = (newEntityType: { label: string; value: string }) => {
        clearHooksAndSearch();
        props.setSelectedEntityType(newEntityType);
    }

    // Pure, memoized derivation of the search suggestion list from (chartData, entitySearchTerm).
    // Because this is derived rather than imperatively set inside an effect, it is always
    // correct regardless of when/how the component (re)mounts.
    const searchResults: Array<DataPoint> = useMemo(() => {
        const searchTerm = props.entitySearchTerm;

        if (!searchTerm || searchTerm.trim() === '') {
            return [];
        }

        const fuse = new Fuse(chartData, {
            keys: ["x"],
            shouldSort: true,
            threshold: 0.3,
            ignoreLocation: true,
        });

        const result = fuse.search(searchTerm);
        let finalResult: Array<DataPoint> = [];

        for (var i = 0; i < result.length; i++) {
            finalResult.push(result[i].item);
        }

        return finalResult.sort((a, b) => a.x.localeCompare(b.x));
    }, [chartData, props.entitySearchTerm]);

    const searchEntityInChart = (searchTerm: string) => {
        props.setEntitySearchTerm(searchTerm);
        setIsEntitySearchSuggestOpen(true);

        if (!searchTerm || searchTerm.trim() === '') {
            closeSuggestionDebounce();
            return;
        }

        closeSuggestionDebounce();
    };

    const selectEntityFromSearch = (selectedEntity: DataPoint) => {
        const entityIndex = chartData.findIndex(item => item.x === selectedEntity.x);

        if (entityIndex !== -1) {
            const targetPageIndex = Math.floor(entityIndex / chartIntervals) * chartIntervals;
            adjustShowingTotal(targetPageIndex);
            props.setEntitySearchTerm(selectedEntity.x);
            setIsEntitySearchSuggestOpen(false);
        }
    };

    const suggestionInteraction = () => {
        setIsEntitySearchSuggestOpen(true);
        closeSuggestionDebounce();
    };

    const handleBarClick = useCallback((eventObj: any, dataObj: any) => {

        if (!isMounted.current) return;

        if (props.selectedAccountType.value !== "REVENUES" && props.selectedAccountType.value !== "EXPENDITURES" && props.selectedAccountType.value !== "BALANCE_SHEET") return

        const clickedIndex = dataObj.index;
        const clickedData = chartData[currentMaxIndex + clickedIndex];

        if (clickedData) {
            props.setSelectedDataPoint(clickedData);

            if (props.selectedFiscalYear.value && props.selectedFiscalYear.value.trim() !== "") {

                const yearValue = Number(props.selectedFiscalYear.value);

                if (Number.isFinite(yearValue)) {
                    var criterion: DataCriterion = {
                        year: yearValue,
                        dataType: props.selectedAccountType.value as 'REVENUES' | 'EXPENDITURES' | 'LONG-TERM_DEBT' | 'POPULATION' | 'BALANCE_SHEET' | null,
                        entityType: "NULL",
                        entityId: clickedData.z ? clickedData.z : 0,
                        group: null,
                    };

                    dispatch(getDataPointNodeByYearEntityAccount(criterion));
                }

            }

            // Switch to pie chart view
            props.setPageMode('PIE_CHART');
        }
    }, [props.selectedAccountType.value, chartData, currentMaxIndex, props.selectedFiscalYear.value, dispatch]);

    // Update barChartConfig to use dynamic height
    const barChartConfig = useMemo(() => ({
        type: "column2d",
        width: "100%",
        height: chartHeight.toString(),
        dataFormat: "json",
        dataSource: {
            chart: {
                xAxisName: "Entities",
                xAxisNameFontColor: "#666666",
                xAxisNameFontBold: "1",
                yAxisName: props.isDollarAmount() ? props.selectedAccountType.label.toString().replace("Explore ", "") + " (USD)" : "",
                yAxisNameFontColor: "#666666",
                yAxisNameFontBold: "1",
                numberPrefix: props.isDollarAmount() ? "$" : "",
                formatNumberScale: "1",
                numberScaleValue: "1000,1000,1000",
                numberScaleUnit: "K,M,B",
                decimals: "2",
                forceDecimals: "2",
                theme: "fusion",
                paletteColors: "#FF8C42",
                plotToolText: "$label: <b>$dataValue</b><br/>Click for details",
            },
            data: displayValues || []
        },
        events: {
            dataPlotClick: handleBarClick
        }
    }), [props.selectedAccountType.label, displayValues, handleBarClick, props.isDollarAmount]);

    React.useEffect(() => {
        const updateChartHeight = () => {
            // Calculate height: 70% of viewport height, clamped between 500 and 850px
            const calculatedHeight = Math.min(850, Math.max(500, window.innerHeight * 0.7));
            setChartHeight(calculatedHeight);
        };

        updateChartHeight();
        window.addEventListener('resize', updateChartHeight);

        return () => {
            isMounted.current = false;
            window.removeEventListener('resize', updateChartHeight);
            dispatch(clearBarChartData());
        }
    }, []);

    React.useEffect(() => {

        if (availableReportYearsSelector.length > 0 && fiscalYearOptions.length == 0) {

            let allReportYears: Array<number> = [];

            for (let i = 0; i < availableReportYearsSelector.length; i++) {
                allReportYears[i] = availableReportYearsSelector[i];
            }

            var sortedReportYears = allReportYears.map((year: any) => ({
                value: year.toString(),
                label: year.toString()
            })).reverse();

            setFiscalYearOptions(sortedReportYears);
            if (props.selectedFiscalYear.value == "default") {
                props.setSelectedFiscalYear(sortedReportYears[0]);
            }
        }

    }, [availableReportYearsSelector])

    React.useEffect(() => {

        fetchDataForSelectedYearAndAccountType(props.selectedFiscalYear.value, props.selectedAccountType.value);

    }, [props.selectedAccountType, props.selectedFiscalYear]);

    // Single source of truth: derive chartData/pagination/display values purely from
    // sortedFilteredData (which is itself a pure function of redux state + selected entity type)
    // and the current search term. This runs correctly on first render after a remount,
    // eliminating the race that previously caused stale/unfiltered data to appear.
    React.useEffect(() => {

        const maxValue = sortedFilteredData.length > 0 ? sortedFilteredData[0].y : 0;
        const minValue = sortedFilteredData.length > 0 ? sortedFilteredData[sortedFilteredData.length - 1].y : 0;
        const topQuantities = sortedFilteredData.slice(0, chartIntervals);

        setChartData(sortedFilteredData);
        setMaxDataValue(maxValue);
        setMinDataValue(minValue);

        if (props.entitySearchTerm && props.entitySearchTerm.trim() !== '') {
            const entityIndex = sortedFilteredData.findIndex(item => item.x === props.entitySearchTerm);
            if (entityIndex !== -1) {
                const targetPageIndex = Math.floor(entityIndex / chartIntervals) * chartIntervals;
                setCurrentMaxIndex(targetPageIndex);
                setFusionChartDataFromDataPoints(sortedFilteredData.slice(targetPageIndex, targetPageIndex + chartIntervals));
            } else {
                setCurrentMaxIndex(0);
                setFusionChartDataFromDataPoints(topQuantities);
            }
        } else {
            setCurrentMaxIndex(0);
            setFusionChartDataFromDataPoints(topQuantities);
        }
    }, [sortedFilteredData]);

    const ControlPanelRender = (<React.Fragment>
        <div style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            height: '100%'
        }}>
            <h5 style={{
                fontFamily: "'Segoe UI', Arial, sans-serif",
                fontWeight: 400,
                marginBottom: '20px'
            }}>
                Chart Options
            </h5>

            <Collapse isOpen={availableReportYearsSelector && availableReportYearsSelector.length > 0}>
                <div className="mb-4">
                    <label style={{
                        fontWeight: 500,
                        marginBottom: '8px',
                        display: 'block'
                    }}>
                        Fiscal Year
                    </label>
                    <Select
                        options={fiscalYearOptions}
                        value={props.selectedFiscalYear}
                        onChange={(e: { label: string; value: string }) => { props.setSelectedFiscalYear(e); fetchDataForSelectedYearAndAccountType(e.value, props.selectedAccountType.value) }}
                        placeholder="Select fiscal year..."
                        disabled={chartData.length === 0}
                    />
                </div>
            </Collapse>

            <div className="mb-4">
                <label style={{
                    fontWeight: 500,
                    marginBottom: '8px',
                    display: 'block'
                }}>
                    Entity Type
                </label>
                <Select
                    options={entityTypeValues}
                    value={props.selectedEntityType}  // Use props
                    disabled={chartData.length === 0}
                    onChange={(e: { label: string; value: string }) => { handleEntityTypeChange(e); }}
                />
            </div>

            <div className="mb-4">
                <FormGroup>
                    <Label style={{
                        fontWeight: 500,
                        marginBottom: '8px',
                        display: 'block'
                    }}>
                        Search Entity
                    </Label>
                    <div style={{ position: 'relative' }}>
                        <Input
                            type="text"
                            value={props.entitySearchTerm}
                            onChange={(e) => searchEntityInChart(e.currentTarget.value)}
                            onFocus={suggestionInteraction}
                            placeholder="Type to search entities..."
                            disabled={chartData.length === 0}
                            style={{ paddingRight: props.entitySearchTerm ? '30px' : '12px' }}
                        />
                        {props.entitySearchTerm && (
                            <button
                                type="button"
                                onClick={() => {
                                    props.setEntitySearchTerm('');
                                    setIsEntitySearchSuggestOpen(false);
                                }}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    lineHeight: '1',
                                    color: '#6c757d',
                                    padding: '0 4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {isEntitySearchSuggestOpen && searchResults && searchResults.length > 0 && (
                        <div
                            onScroll={suggestionInteraction}
                            onMouseEnter={suggestionInteraction}
                            onMouseMove={suggestionInteraction}
                            style={{
                                height: "10em",
                                overflowY: "scroll",
                                position: "absolute",
                                backgroundColor: "white",
                                zIndex: 1000,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                marginTop: "2px",
                                width: "calc(100% - 40px)",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                            }}>
                            {searchResults.map((result, index) => (
                                <div
                                    key={index}
                                    onClick={() => selectEntityFromSearch(result)}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        borderBottom: "1px solid #f0f0f0"
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                >
                                    <div style={{ fontWeight: 500 }}>{result.x}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {props.formatNumericValue(result.y)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </FormGroup>
            </div>

            <div>
                <p>
                </p>
                <p>
                    <button className="btn btn-primary btn-sm"
                        disabled={currentMaxIndex - chartIntervals < 0 || chartData.length === 0}
                        onClick={() => { adjustShowingTotal(currentMaxIndex - chartIntervals) }}>
                        Previous 10
                    </button>
                    &nbsp; &nbsp;
                    <button className="btn btn-primary btn-sm"
                        disabled={currentMaxIndex + chartIntervals >= chartData.length || chartData.length === 0}
                        onClick={() => { adjustShowingTotal(currentMaxIndex + chartIntervals) }}>
                        Next 10
                    </button>
                </p>
            </div>

            <div className="mb-3">
                <label style={{
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: '8px'
                }}>
                    Chart Information
                </label>
                <div style={{ fontSize: '14px', color: '#666' }}>
                    <p><strong>Total Entities:</strong> {chartData.length.toLocaleString()}</p>
                    <p><strong>Displaying:</strong> {(currentMaxIndex + 1).toLocaleString()} - {(Math.min(currentMaxIndex + chartIntervals, chartData.length)).toLocaleString()}</p>
                    <p><strong>Highest:</strong> {props.formatNumericValue(maxDataValue)}</p>
                    <p><strong>Lowest:</strong> {props.formatNumericValue(minDataValue)}</p>
                </div>
            </div>
        </div>
    </React.Fragment>);

    // Memoize getCaptionValue to prevent recreation on every render
    const getNumericCaption = useCallback(() => {
        let result: string = "";
        if (chartData && chartData.length > 0) {
            result = "Fiscal Year " + `${props.selectedFiscalYear.value}` + " - " + `${props.formatNumericValue(chartData.reduce((sum, { y }) => sum + y, 0))}`;
        }
        return result;
    }, [props.selectedDataPoint, props.selectedAccountType.label, chartData]);

    const BarChartRender = (<React.Fragment>
        <Row>
            <Col md="10">
                <Row>
                    <Col md="12">
                        <h3 style={{ paddingLeft: ".75rem" }}>{props.selectedAccountType ? props.selectedAccountType.label.replace("Explore ", "") : ""}</h3>
                        <h5 style={{ paddingLeft: ".75rem" }}>{getNumericCaption()}</h5>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div style={{ position: 'relative', overflow: 'visible' }}>
                            <ReactFC {...barChartConfig} />
                            {loadingStatusSelector === 'loading' && chartData.length === 0 && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#ffffff',
                                    fontSize: '18px',
                                    color: '#666',
                                    zIndex: 10
                                }}>

                                    <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                        type="grow" color="warning"
                                    /> &nbsp;
                                    Loading data...
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </Col>
            <Col md="2">
                <Row style={{ height: "45em", overflowY: 'scroll' }}>
                    <Col>
                        {ControlPanelRender}
                    </Col>
                </Row>
            </Col>
        </Row>
    </React.Fragment>)

    return (
        <React.Fragment>
            {BarChartRender}
        </React.Fragment>
    );
}

export default BarChartView;